import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { fetchHaciendaConsultById, pickPrimaryActivityCode } from '@/services/hacienda/haciendaConsultById';
import { Combobox } from '@/components/ui/combobox';
import { type Client, type CustomerInfo } from '@/types';
import CustomerIdentityFields from './CustomerIdentityFields';
import CustomerAddressFields from './CustomerAddressFields';

interface CustomerInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (customerInfo: CustomerInfo) => void;
  defaultValues?: CustomerInfo;
  isSubmitting?: boolean;
  isClientTab?: boolean;
  existingClients?: Client[];
}
//Required by Hacienda
const DEFAULT_OTHER_ADDRESS = 'Costa Rica';
const emptyCustomerInfo: CustomerInfo = {
  name: '',
  tipo: '',
  identification: '',
  activityCode: '',
  address: {
    province: '',
    canton: '',
    district: '',
    other: DEFAULT_OTHER_ADDRESS,
  },
  phone: {
    country_code: '506',
    number: '88888888',
  },
  email: '',
};

const CustomerInfoModal: React.FC<CustomerInfoModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isSubmitting = false,
  isClientTab = false,
  existingClients,
}) => {
  const { translator } = useContext(LanguageProviderContext);
  const [customerForm, setCustomerForm] = useState<CustomerInfo>(
    defaultValues ?? emptyCustomerInfo
  );
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const prevOpenRef = useRef(open);

  useEffect(() => {
    let isActive = true;
    const identification = customerForm.identification.trim();

    if (!identification) {
      return undefined;
    }

    const handler = setTimeout(async () => {
      const response = await fetchHaciendaConsultById(identification);

      if (response && isActive) {
        setCustomerForm((prev) => ({
          ...prev,
          name: response.nombre,
          tipo: response.tipoIdentificacion,
          activityCode: pickPrimaryActivityCode(response.actividades),
        }));
      }
    }, 400);

    return () => {
      isActive = false;
      clearTimeout(handler);
    };
  }, [customerForm.identification]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!wasOpen && open) {
      setCustomerForm(defaultValues ?? emptyCustomerInfo);
      setSelectedClientId(undefined);
      setIdentityError(null);
      setAddressError(null);
    }
  }, [open, defaultValues]);

  const patchForm = (patch: Partial<CustomerInfo>) => {
    setIdentityError(null);
    setCustomerForm((prev) => ({ ...prev, ...patch }));
  };

  const patchAddress = (patch: Partial<CustomerInfo['address']>) => {
    setAddressError(null);
    setCustomerForm((prev) => ({
      ...prev,
      address: { ...prev.address, ...patch },
    }));
  };

  const submitCustomer = () => {
    if (!customerForm.identification?.trim()) {
      setIdentityError(translator('Invoices.requiredIdentification'));
      return;
    }
    if (!customerForm.name?.trim()) {
      setIdentityError(translator('Invoices.requiredName'));
      return;
    }
    if (!customerForm.email?.trim()) {
      setIdentityError(translator('Invoices.requiredEmail'));
      return;
    }
    if (!customerForm.tipo?.trim()) {
      setIdentityError(translator('Invoices.requiredTipo'));
      return;
    }
    const { province, canton, district } = customerForm.address;
    if (!province?.trim() || !canton?.trim() || !district?.trim()) {
      setAddressError(translator('Invoices.requiredAddress'));
      return;
    }
    setIdentityError(null);
    setAddressError(null);
    onSubmit(customerForm);
  };

  const clientOptions =
    existingClients?.map((c) => ({
      value: c.id,
      label: c.name,
      description: c.identification ?? '',
    })) ?? [];

  const handleClientSelect = (selectedId: string) => {
    setSelectedClientId(selectedId);
    const selected = existingClients?.find((c) => c.id === selectedId);
    if (!selected) return;

    setCustomerForm({
      name: selected.name,
      tipo: selected.tipo ?? '',
      identification: selected.identification ?? '',
      activityCode: selected.activityCode ?? '',
      address: selected.address ?? {
        province: '',
        canton: '',
        district: '',
        other: '',
      },
      phone: selected.phone ?? { country_code: '506', number: '88888888' },
      email: selected.email ?? '',
    });
    setIdentityError(null);
    setAddressError(null);
  };

  const isDisabledSaveButton =
    isSubmitting ||
    !customerForm.identification?.trim() ||
    !customerForm.name?.trim() ||
    !customerForm.email?.trim() ||
    !customerForm.tipo?.trim() ||
    !customerForm.address.province?.trim() ||
    !customerForm.address.canton?.trim() ||
    !customerForm.address.district?.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-none max-h-[calc(100dvh-1rem)] md:max-w-4xl flex flex-col p-4 lg:p-6 overflow-hidden">
        <DialogHeader className="shrink-0 pb-2">
          <DialogTitle>
            {!isClientTab
              ? translator('Invoices.customerInfoTitle')
              : translator('Clients.client')}
          </DialogTitle>
          <DialogDescription>
            {!isClientTab
              ? translator('Invoices.customerInfoDescription')
              : translator('Clients.clientDescription')}
            <br />
            {translator('Products.requiredFields')}
          </DialogDescription>
          {!isClientTab && existingClients && existingClients.length > 0 && (
            <div className="pt-2">
              <Combobox
                options={clientOptions}
                value={selectedClientId}
                onValueChange={handleClientSelect}
                placeholder={translator('Invoices.invoiceSearchClientPlaceholder')}
                searchPlaceholder={translator('Invoices.invoiceSearchClientPlaceholder')}
                emptyMessage={translator('Clients.noClientsFound') || 'No client found.'}
              />
            </div>
          )}
        </DialogHeader>
        <form
          className="flex-1 min-h-0 overflow-y-auto pr-2 pl-2 -mx-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-muted-foreground/50 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomerIdentityFields
              value={customerForm}
              onChange={patchForm}
              error={identityError}
              showPhone={isClientTab}
            />
            <CustomerAddressFields
              value={customerForm.address}
              onChange={patchAddress}
              error={addressError}
            />
          </div>
          {isClientTab ? (
            <DialogFooter className="mt-6">
              <Button
                type="button"
                className="w-full"
                disabled={isDisabledSaveButton}
                onClick={submitCustomer}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {translator('Global.saving')}
                  </>
                ) : (
                  translator('Global.save')
                )}
              </Button>
            </DialogFooter>
          ) : (
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                {translator('Global.cancel')}
              </Button>
              <Button type="button" disabled={isDisabledSaveButton} onClick={submitCustomer}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {translator('Invoices.creatingInvoice')}
                  </>
                ) : (
                  translator('Invoices.createInvoice')
                )}
              </Button>
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerInfoModal;
